import { Form, FormContext } from '@hai/ui-react';
import { ButtonStateType } from '@hai/ui-react/dist/types';
import { FormikProps } from 'formik';
import { clone } from 'ramda';
import React, { Ref, forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { triggerEvent } from 'src/js/events';
import { isNilOrEmpty } from 'src/js/util/global-util';
import { submitAndValidate } from 'src/js/validator/validator';

import { MetadataEditForm } from './metadata-edit-form';
import { MetadataViewModel, metadataValidationSchema } from './metadata-models';
import metadataModels, { createMetadata, updateMetadata } from './metadata-store';

interface Props {
  model: MetadataViewModel;
  onCancel?: VoidFunction;
}

export interface MetadataAddFormRef {
  submitAddMetadata: () => Promise<void>;
}

export const MetadataSettings = forwardRef((props: Props, ref: Ref<MetadataAddFormRef>) => {
  const { model, onCancel } = props;
  const { t } = useTranslation();

  const formRef = useRef<FormikProps<MetadataViewModel>>();

  const [settings, setSettings] = useState<MetadataViewModel>(model);
  const [buttonState, setButtonState] = useState<ButtonStateType>(undefined);
  const newMetadata = settings.id === -1;

  const handleSubmit = (values: MetadataViewModel) => {
    setButtonState('pending');

    const data: any = clone(values);
    if (!data.klvFilteringEnabled) {
      data.uasTags = [];
    }

    if (!data.klvFilteringSecurityEnabled) {
      data.uasSecurityTags = [];
    }

    if (isNilOrEmpty(data.relays)) {
      data.enableCotRelay = false;
    }

    if (newMetadata) {
      return createMetadata(data, t).then((res) => {
        setButtonState(undefined);
        if (res.ok) {
          triggerEvent('preset-update');
        }
        return res;
      });
    } else {
      return updateMetadata(data, t).then((res) => {
        if (res.ok) {
          const updatedModel = metadataModels.find((m) => m.id === model.id);
          setSettings(updatedModel); // update edit view with newest model
          formRef.current.resetForm({ values: updatedModel });
          triggerEvent('preset-update');
        }
        setButtonState(undefined);
        return res;
      });
    }
  };

  const submitAddMetadata = () => {
    return submitAndValidate(formRef.current, t);
  };

  useImperativeHandle(ref, () => ({
    submitAddMetadata,
  }));

  return (
    <Form
      initialValues={settings}
      defaultValidation={true}
      handleSubmit={handleSubmit}
      className="hai-mt-n8"
      restValidationProps={{
        enableReinitialize: false, // "false" requires manual resetForm() above. We can then do partial update of the form while it's opened.
        validationSchema: metadataValidationSchema(t),
        innerRef: formRef,
      }}
    >
      <FormContext.Consumer>
        {(formContext: FormikProps<MetadataViewModel>) => {
          return (
            <MetadataEditForm
              formikProps={formContext}
              formRef={formRef}
              applyButtonState={buttonState}
              onCancel={onCancel}
            />
          );
        }}
      </FormContext.Consumer>
    </Form>
  );
});

MetadataSettings.displayName = 'MetadataSettings';
